import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  getV1CompaniesCompanyIdOrdersOrderIdTimeline,
  GetV1CompaniesCompanyIdOrdersOrderIdTimelineData,
  PrincipalType,
  User_Read_Minimal,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useEffect, useState } from 'react';

import { createdBy } from '~components/Job/AuditDetails/utils';
import { Order } from '~hooks/useOrders/models';
import { OrderTimeline } from '~hooks/useOrders/models';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { ObjectChanges } from './ObjectChanges';
import { TIMELINE_EXCLUDED_KEYS } from './util';

interface OrderAuditLogProps {
  order: Order;
}

const ORDER_TIMELINE_PAGE_LIMIT = 5;

const OrderAuditLog = ({ order }: OrderAuditLogProps) => {
  const theme = useTheme();
  const [timeline, setTimeline] = useState<OrderTimeline[]>([]);
  const [after, setAfter] = useState<string | null>(null);

  const { userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);

  const getOrderTimeline = async (orderId: string) => {
    try {
      const query: GetV1CompaniesCompanyIdOrdersOrderIdTimelineData['query'] = {
        'page[limit]': ORDER_TIMELINE_PAGE_LIMIT,
      };
      if (after) {
        query['page[after]'] = after;
      }
      const response = await getV1CompaniesCompanyIdOrdersOrderIdTimeline({
        path: {
          'company-id': order.company?.id ?? effectiveCompanyId,
          'order-id': orderId,
        },
        query,
      });
      const pagination = extractPagination(response);
      setAfter(pagination.after ?? null);

      return response.data.data.map(OrderTimeline.parse);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.orders.failed_to_fetch_timeline'),
      );
    }
  };
  const fetchOrderTimeline = async (orderId: string) => {
    const newTimeline = await getOrderTimeline(orderId);
    const filteredTimeline = newTimeline?.filter(
      (timeline) =>
        !Object.keys(timeline.objectChanges).every((key) =>
          TIMELINE_EXCLUDED_KEYS.includes(key),
        ),
    );
    setTimeline((prevTimeline) => [...prevTimeline, ...(filteredTimeline ?? [])]);
  };

  useEffect(() => {
    fetchOrderTimeline(order.id);
  }, []);

  return (
    <Timeline
      sx={{
        p: 0,
        pt: 1,
        px: 0,
        mt: 0,
        mb: 2,
        [`& .MuiTimelineOppositeContent-root`]: {
          flex: 0.5,
          display: timeline.length > 1 ? 'flex' : 'none',
        },
        [`& .MuiTimelineItem-root`]: {
          minHeight: theme.spacing(4),
          pl: 2,
        },
        [`& .MuiTimelineItem-positionRight:before`]: {
          flex: 0,
          p: 0,
        },
        [`& .MuiTimelineConnector-root`]: {
          my: -1.5,
          width: '4px',
          height: '48px',
          backgroundColor: theme.brandV2.colors.treadYellow,
        },
        [`& .MuiTimelineDot-root`]: {
          backgroundColor: theme.brandV2.colors.treadYellow,
          border: `2px solid ${theme.brandV2.colors.treadYellowDark}`,
          boxShadow: 'none',
          width: '16px',
          height: '16px',
        },
      }}
    >
      {timeline.map((item, index) => {
        return (
          <TimelineItem key={`${order.id}_${index}_${item.createdAt.toISOString()}`}>
            <TimelineSeparator>
              <TimelineDot />
              {index < timeline.length - 1 ? <TimelineConnector /> : null}
            </TimelineSeparator>
            <TimelineContent
              sx={{
                display: 'flex',
                gap: 4,
              }}
            >
              <Box
                sx={{
                  minWidth: '70px',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    color: theme.brandV2.colors.treadBlack,
                    fontWeight: 600,
                  }}
                >
                  {dayjs(item.createdAt).tz().format('h:mm A')}
                </Typography>
                <Typography
                  variant={'body2'}
                  sx={{
                    color: theme.brandV2.colors.treadGray2,
                  }}
                >
                  {dayjs(item.createdAt).tz().format('MMM D, YYYY')}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '150px',
                  minWidth: '150px',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    color: theme.brandV2.colors.treadBlack,
                    fontWeight: 600,
                  }}
                >
                  {createdBy(item.createdBy)}
                </Typography>
                <Typography
                  variant={'body2'}
                  sx={{
                    color: theme.brandV2.colors.treadGray2,
                  }}
                >
                  {item.createdBy.type === PrincipalType.USER
                    ? `${(item.createdBy as User_Read_Minimal).company.legal_name}`
                    : ''}
                </Typography>
              </Box>
              <Box>
                <Typography
                  component="div"
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    color: theme.brandV2.colors.treadBlack,
                    fontWeight: 600,
                  }}
                >
                  <ObjectChanges
                    objectChanges={item.objectChanges}
                    isLastChange={!after && index === timeline.length - 1}
                  />
                </Typography>
                <Typography
                  variant={'body2'}
                  sx={{
                    color: theme.brandV2.colors.treadGray2,
                  }}
                ></Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
        );
      })}
      {after && (
        <Button variant="text" onClick={() => fetchOrderTimeline(order.id)}>
          {t('actions.show_more')}
        </Button>
      )}
    </Timeline>
  );
};

export default OrderAuditLog;
